import React, { useEffect, useRef } from "react";
import { IFrame, ICanvas, IPainter } from "./animationTypes";

// frames will follow a certain pattern that we can abstract here
const buildDrawer = ({ canvas, context, image }: ICanvas) => (
  frame?: IFrame
) => {
  if (frame === undefined) return;
  const { w, h, x, y } = frame;
  const { width: cW, height: cH } = canvas;
  // place sprite at the center-bottom of the canvas [(cH - h)/2, cH - h]
  context.drawImage(image, x, y, w, h, (cW - w) / 2, cH - h, w, h);
};

function setup(
  imageRef: React.RefObject<CanvasImageSource>,
  canvasRef: React.RefObject<HTMLCanvasElement>
): IPainter | undefined {
  const image = imageRef.current;
  if (!image) return undefined;
  const canvas = canvasRef.current;
  if (!canvas) return undefined;
  const context = canvas.getContext("2d");
  if (!context) return undefined;

  return {
    draw: buildDrawer({ canvas, context, image }),
    clear: () => context.clearRect(0, 0, canvas.width, canvas.height)
  };
}
// FIXME split this function into smaller ones
/* eslint-disable max-lines-per-function,max-statements */
function animate(
  imageRef: React.RefObject<CanvasImageSource>,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  frames: IFrame[],
  effects: IFrame[]
) {
  let requestId: number;
  const cancelAnimation = () => cancelAnimationFrame(requestId);

  const painter = setup(imageRef, canvasRef);
  if (painter === undefined) return cancelAnimation;

  // initialize useful variables to simplify our life later on
  let [i, j, frameCount] = [0, 0, 0];
  const FPS = 120;

  function render() {
    if (painter === undefined) return;

    // handle frame count
    frameCount += 1;
    if (frameCount < 1000 / FPS) {
      requestId = requestAnimationFrame(render);
      return;
    }
    frameCount = 0;

    // actually draw the frame
    painter.clear();
    painter.draw(frames[i]);
    painter.draw(effects[j]);
    // base sprite keeps playing
    i = (i + 1) % frames.length;
    // effect only plays once
    if (j < effects.length) j += 1;
    requestId = requestAnimationFrame(render);
  }
  render();
  return cancelAnimation;
}

function useAnimation(frames: IFrame[], effects: IFrame[]) {
  // we need a ref for the spritesheet image and another to draw inside the canvas
  const imageRef = useRef(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    return animate(imageRef, canvasRef, frames, effects);
  });
  return { canvasRef, imageRef };
}

export default useAnimation;
